import React, { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { getCdpActiveVisitors, getCdpRecentEvents, getCdpEnrichVisitor } from "../../api";
import { useView } from "../../contexts/ViewContext";
import { fmtLocation } from "../../utils/format";

/* ───────────────────────────── constants ───────────────────────────── */
const MAX_CARDS = 50;
const BATCH_INTERVAL = 2000;
const POLL_INTERVAL = 30000;

const VISIBLE_EVENTS = new Set([
  "page_view", "book_now_click", "booking_completed",
  "phone_click", "video_play", "email_capture", "form_submit",
]);

/* ───────────────────────────── helpers ─────────────────────────────── */
function relativeTime(ts) {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function stripDomain(url) {
  if (!url) return "";
  try { return new URL(url).pathname; } catch { return url; }
}

function truncate(str, max = 35) {
  if (!str || str.length <= max) return str;
  return str.slice(0, max) + "…";
}

function maskEmail(email) {
  if (!email) return null;
  const [local, domain] = email.split("@");
  if (!domain) return email;
  return `${local.slice(0, 3)}***@${domain}`;
}

function ordSuffix(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

// Convert URL path → human-readable page name
// /book/verde-ranch-rv  → "Verde Ranch RV"
// /rv-sites/            → "RV Sites"
// /                     → "Homepage"
function prettyPageName(url) {
  if (!url) return "Homepage";
  const path = (stripDomain(url) || "").replace(/^\/+|\/+$/g, "");
  if (!path) return "Homepage";
  const last = path.split("/").pop();
  const cleaned = decodeURIComponent(last.split("?")[0].split("#")[0]);
  const spaced = cleaned.replace(/[-_]+/g, " ").trim();
  if (!spaced) return "Homepage";
  const titled = spaced.replace(/\w\S*/g, (w) => {
    if (w.length <= 3 && w === w.toUpperCase()) return w; // keep acronyms (RV, KOA)
    return w[0].toUpperCase() + w.slice(1).toLowerCase();
  });
  return truncate(titled, 28);
}

function deviceLabel(d) {
  if (d === "mobile") return "Mobile";
  if (d === "tablet") return "Tablet";
  if (d === "desktop") return "Desktop";
  return null;
}

/* ─── tiny inline icons ─────────────────────────────────────────────── */
const SVG = (props) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...props} />
);
const PinIcon = () => <SVG><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></SVG>;
const PageIcon = () => <SVG><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></SVG>;
const MobileIcon = () => <SVG><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18"/></SVG>;
const DesktopIcon = () => <SVG><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></SVG>;
const RepeatIcon = () => <SVG><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></SVG>;

/* ─── card visual configs per event type ────────────────────────────── */
// Each style: border (left), chipBg (header pill), chipText (header pill text), title, emoji
const CARD_STYLES = {
  book_now_click:      { border: "border-emerald-500", chipBg: "bg-emerald-100 dark:bg-emerald-900/40", chipText: "text-emerald-700 dark:text-emerald-300", title: "Book Now Click",   emoji: "🛒" },
  booking_completed:   { border: "border-amber-500",   chipBg: "bg-amber-100 dark:bg-amber-900/40",     chipText: "text-amber-700 dark:text-amber-300",     title: "New Booking",       emoji: "🎉", featured: true },
  phone_click:         { border: "border-blue-500",    chipBg: "bg-blue-100 dark:bg-blue-900/40",       chipText: "text-blue-700 dark:text-blue-300",       title: "Phone Click",       emoji: "📞" },
  email_capture:       { border: "border-purple-500",  chipBg: "bg-purple-100 dark:bg-purple-900/40",   chipText: "text-purple-700 dark:text-purple-300",   title: "Email Captured",    emoji: "📧" },
  video_play:          { border: "border-teal-500",    chipBg: "bg-teal-100 dark:bg-teal-900/40",       chipText: "text-teal-700 dark:text-teal-300",       title: "Video Play",        emoji: "🎬" },
  form_submit:         { border: "border-blue-400",    chipBg: "bg-blue-100 dark:bg-blue-900/40",       chipText: "text-blue-700 dark:text-blue-300",       title: "Form Submitted",    emoji: "📝" },
  page_view_known:     { border: "border-purple-400",  chipBg: "bg-purple-100 dark:bg-purple-900/40",   chipText: "text-purple-700 dark:text-purple-300",   title: null,                emoji: "👤" },
  page_view_returning: { border: "border-blue-400",    chipBg: "bg-blue-100 dark:bg-blue-900/40",       chipText: "text-blue-700 dark:text-blue-300",       title: "Returning",         emoji: "🔄" },
  page_view_default:   { border: "border-gray-300 dark:border-gray-600", chipBg: "bg-gray-100 dark:bg-gray-700/60", chipText: "text-gray-600 dark:text-gray-300", title: "New Visitor", emoji: "👋" },
};

/* ──────────────────────────── feed card ────────────────────────────── */
function FeedCard({ event, enrichment }) {
  const e = event;
  const en = enrichment || {};
  const location = fmtLocation(en.city, en.region, en.country);
  const visitNum = (en.previous_sessions || 0) + 1;
  const isReturning = visitNum > 1;
  const visitLabel = isReturning ? `${visitNum}${ordSuffix(visitNum)} visit` : null;
  const channelLabel = en.channel || "Direct";
  const device = en.device || null;
  const dLabel = deviceLabel(device);
  const pageName = prettyPageName(e.page_url);

  // pick the right style
  let styleKey = e.event_type;
  let title;
  if (e.event_type === "page_view") {
    if (en.email) {
      styleKey = "page_view_known";
      title = maskEmail(en.email);
    } else if (isReturning) {
      styleKey = "page_view_returning";
      title = CARD_STYLES.page_view_returning.title;
    } else {
      styleKey = "page_view_default";
      title = CARD_STYLES.page_view_default.title;
    }
  } else {
    title = CARD_STYLES[styleKey]?.title || e.event_type;
  }
  const style = CARD_STYLES[styleKey] || CARD_STYLES.page_view_default;
  const featured = !!style.featured;

  return (
    <div
      className={`relative border-l-4 ${style.border} ${
        featured ? "bg-amber-50 dark:bg-amber-900/15 shadow-sm" : "bg-white dark:bg-gray-800/60"
      } rounded-r-lg px-3 py-2.5 animate-slideIn hover:shadow-sm transition-shadow`}
    >
      {/* Header: chip + time */}
      <div className="flex items-center justify-between gap-2">
        <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ${style.chipBg} ${style.chipText} text-[10px] font-bold uppercase tracking-wide`}>
          <span className="text-[10px] leading-none">{style.emoji}</span>
          <span className="truncate max-w-[140px] normal-case tracking-normal">{title}</span>
        </div>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 shrink-0">
          {relativeTime(e.event_timestamp)}
        </span>
      </div>

      {/* Primary line: location (or page if no location) */}
      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-800 dark:text-gray-200 font-medium truncate">
        {location ? (
          <>
            <span className="text-gray-400 dark:text-gray-500 shrink-0"><PinIcon /></span>
            <span className="truncate">{location}</span>
          </>
        ) : (
          <>
            <span className="text-gray-400 dark:text-gray-500 shrink-0"><PageIcon /></span>
            <span className="truncate">{pageName}</span>
          </>
        )}
      </div>

      {/* Secondary line: page (only if location was already shown) */}
      {location && (
        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 truncate">
          <span className="shrink-0 opacity-60"><PageIcon /></span>
          <span className="truncate">{pageName}</span>
        </div>
      )}

      {/* Footer: meta tags */}
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700/50 font-medium">
          {channelLabel}
        </span>
        {dLabel && (
          <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700/50">
            <span className="opacity-70">{device === "mobile" || device === "tablet" ? <MobileIcon /> : <DesktopIcon />}</span>
            {dLabel}
          </span>
        )}
        {visitLabel && (
          <span className="inline-flex items-center gap-1 text-[10px] text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 font-medium">
            <RepeatIcon />
            {visitLabel}
          </span>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════ LIVE FEED ═══════════════════════════════ */
export default function LiveFeed({ clientId, channelColors, panelHeight }) {
  const { showNewMenu } = useView();
  // The fixed LiveFeed sits flush against the NavSidebar's right edge. The
  // grouped sidebar is w-56 (224px), the legacy one is w-20 (80px).
  const navSidebarWidth = showNewMenu ? 224 : 80;
  const [activeCount, setActiveCount] = useState(0);
  const [knownCount, setKnownCount] = useState(0);
  const [returningCount, setReturningCount] = useState(0);
  const [feedCards, setFeedCards] = useState([]);

  const enrichmentCache = useRef(new Map());
  const batchBuffer = useRef([]);
  const batchTimer = useRef(null);
  const channelRef = useRef(null);

  // Poll active visitor count every 30s
  useEffect(() => {
    if (!clientId) return;
    const poll = async () => {
      try {
        const resp = await getCdpActiveVisitors(clientId);
        const d = resp?.data;
        if (d) {
          setActiveCount(d.active_visitors || 0);
          setKnownCount(d.known_visitors || 0);
          setReturningCount(d.returning_visitors || 0);
        }
      } catch (e) { console.error("Active visitors poll error:", e); }
    };
    poll();
    const interval = setInterval(() => { if (!document.hidden) poll(); }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [clientId]);

  const enrichCvId = useCallback(
    async (cvId) => {
      if (!cvId || !clientId) return {};
      if (enrichmentCache.current.has(cvId)) return enrichmentCache.current.get(cvId);
      try {
        const resp = await getCdpEnrichVisitor(clientId, cvId);
        const data = resp?.data || {};
        enrichmentCache.current.set(cvId, data);
        return data;
      } catch {
        enrichmentCache.current.set(cvId, {});
        return {};
      }
    },
    [clientId]
  );

  const processBatch = useCallback(async () => {
    const events = [...batchBuffer.current];
    batchBuffer.current = [];
    if (events.length === 0) return;
    const visible = events.filter((e) => VISIBLE_EVENTS.has(e.event_type));
    if (visible.length === 0) return;
    const cvIds = [...new Set(visible.map((e) => e.cv_id).filter(Boolean))];
    await Promise.all(cvIds.map(enrichCvId));
    setFeedCards((prev) => {
      const newCards = visible.map((e) => ({
        id: `${e.event_type}-${e.cv_id}-${e.event_timestamp}-${Math.random()}`,
        event: e,
        enrichment: enrichmentCache.current.get(e.cv_id) || {},
      }));
      return [...newCards.reverse(), ...prev].slice(0, MAX_CARDS);
    });
  }, [enrichCvId]);

  useEffect(() => {
    if (!clientId) return;
    const channel = supabase
      .channel(`live-events-${clientId}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "tracking_events",
        filter: `client_id=eq.${clientId}`,
      }, (payload) => {
        const newEvent = payload.new;
        if (!newEvent) return;
        batchBuffer.current.push(newEvent);
        if (!batchTimer.current) {
          batchTimer.current = setTimeout(() => {
            batchTimer.current = null;
            processBatch();
          }, BATCH_INTERVAL);
        }
      })
      .subscribe();
    channelRef.current = channel;
    return () => {
      if (batchTimer.current) clearTimeout(batchTimer.current);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [clientId, processBatch]);

  useEffect(() => {
    enrichmentCache.current.clear();
    setFeedCards([]);
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    const loadRecent = async () => {
      try {
        const resp = await getCdpRecentEvents(clientId, MAX_CARDS);
        const events = resp?.data || [];
        if (cancelled || events.length === 0) return;
        const cvIds = [...new Set(events.map((e) => e.cv_id).filter(Boolean))];
        await Promise.all(cvIds.map(enrichCvId));
        if (cancelled) return;
        setFeedCards(
          events.map((e) => ({
            id: `seed-${e.event_type}-${e.cv_id}-${e.event_timestamp}-${Math.random()}`,
            event: e,
            enrichment: enrichmentCache.current.get(e.cv_id) || {},
          }))
        );
      } catch (err) {
        console.error("Failed to load recent events:", err);
      }
    };
    loadRecent();
    return () => { cancelled = true; };
  }, [clientId, enrichCvId]);

  return (
    <>
    <aside
      className="hidden lg:flex flex-col fixed border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 z-20"
      style={{
        width: 320,
        left: `${navSidebarWidth}px`,
        top: 'var(--app-header-h, 72px)',
        height: 'calc(100vh - var(--app-header-h, 72px))',
      }}
    >
      {/* Counter header */}
      <div className="px-4 pt-5 pb-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="flex items-baseline gap-2">
          <span className="relative flex h-2.5 w-2.5 self-center">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </span>
          <span className="text-gray-900 dark:text-white font-bold text-2xl leading-none">{activeCount}</span>
          <span className="text-gray-500 dark:text-gray-400 text-sm">on your site now</span>
        </div>
        <div className="text-gray-400 dark:text-gray-500 text-xs mt-1.5 flex items-center gap-2">
          {returningCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
              {returningCount} returning
            </span>
          )}
          {knownCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
              {knownCount} known
            </span>
          )}
          {returningCount === 0 && knownCount === 0 && <span>Live visitor feed</span>}
        </div>
      </div>

      {/* Feed cards */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {feedCards.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-600 text-sm">No visitors right now.</div>
            <div className="text-gray-300 dark:text-gray-700 text-xs mt-1">Your live feed will show activity as people visit your site.</div>
          </div>
        ) : (
          feedCards.map((card) => (
            <FeedCard key={card.id} event={card.event} enrichment={card.enrichment} />
          ))
        )}
      </div>
    </aside>
    {/* Reserves layout space in the flex row so <main> doesn't overlap the fixed LiveFeed. */}
    <div className="hidden lg:block shrink-0" style={{ width: 320 }} aria-hidden="true" />
    </>
  );
}
